const EXTENSION_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  txt: 'text/plain',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  md: 'text/markdown',
  log: 'text/plain',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
  xls: 'application/vnd.ms-excel',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  heic: 'image/heic',
  heif: 'image/heif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
};

const WORD_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.ms-word',
]);

const GENERIC_MIME_TYPES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'text/plain',
  'application/x-www-form-urlencoded',
]);

export function normalizeMimeType(mimeType: string | null | undefined): string {
  return (mimeType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
}

export function mimeTypeFromFilename(filename: string | null | undefined): string {
  const base = (filename ?? '').split(/[\\/]/).pop() ?? '';
  const ext = base.includes('.') ? (base.split('.').pop()?.toLowerCase() ?? '') : '';
  return EXTENSION_MIME[ext] ?? '';
}

export function isGenericMimeType(mimeType: string | null | undefined): boolean {
  return GENERIC_MIME_TYPES.has(normalizeMimeType(mimeType));
}

/** Prefer a real stored type; fall back to the filename when Storage/DB stored a generic MIME. */
export function resolveStoredDocumentMimeType(input: {
  mimeType?: string | null;
  filename?: string | null;
}): string {
  const stored = normalizeMimeType(input.mimeType);
  const fromName = mimeTypeFromFilename(input.filename);
  if (stored && !isGenericMimeType(stored)) return stored;
  return fromName || stored;
}

export function resolveFileMimeType(file: File): string {
  return resolveStoredDocumentMimeType({ mimeType: file.type, filename: file.name });
}

/** Storage object metadata. Safari often sends File.type as empty for PDFs. */
export function documentStorageUploadOptions(file: File): {
  contentType: string;
  upsert: false;
} {
  const contentType = resolveFileMimeType(file);
  return {
    contentType: contentType === 'text/plain' ? 'text/plain;charset=utf-8' : contentType,
    upsert: false,
  };
}

const NON_DISPLAYABLE_IMAGE_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
  'image/tiff',
]);

export function isInlinePreviewable(mimeType: string): boolean {
  if (NON_DISPLAYABLE_IMAGE_TYPES.has(mimeType)) return false;
  return (
    mimeType.startsWith('image/') ||
    mimeType.startsWith('video/') ||
    mimeType === 'application/pdf' ||
    mimeType === 'text/plain'
  );
}

export function isWordDocumentMime(mimeType: string | null | undefined): boolean {
  return WORD_MIME_TYPES.has(normalizeMimeType(mimeType));
}
